//session.service
import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { OrganizationMembership } from './membership.model';

export interface SessionData {
  id: string;
  workEmail: string;
  roles: string[];
  name: string;
  token: string;
  accessToken: string;
  accountType: string;
  idToken: string;
  userId: string;
  hospitalId: string;
  organizationId?: string; // FHIR Organization ID
  activeHospitalFhirId?: string; 
  activeHospitalName?: string;
  role: string;
  isLoggedIn: boolean;
  loginMethod: string;
  expiresAt: number;
  rolePermissionsJson?: string;
  isSuperAdmin?: boolean; // Can access all organizations
  isDefaultPassword?: boolean;
  fhirResourceId?: string; // Patient or Practitioner FHIR ID
  refreshToken?: string;
  photo?: Array<{ url?: string; contentType?: string; title?: string }>;
  telecom?: any[];
  address?: any[];
  gender?: string;
  birthDate?: string;
   isGlobal?: boolean;
  qualification?: any[];
  organizationName?: string;
  memberships?: OrganizationMembership[];
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly SESSION_KEY = 'app_session';
  private readonly ENCRYPTION_KEY = this.generateEncryptionKey();
  private readonly SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours

  private generateEncryptionKey(): string {

    return 'xFL6Xc7TLGwI+O/INMSY2PV/wNIZxSGC53cmh1TVu9E=';
  }

  private encrypt(data: string): string {
    return CryptoJS.AES.encrypt(data, this.ENCRYPTION_KEY).toString();
  }

  private decrypt(encryptedData: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedData, this.ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  setSession(data: Partial<SessionData>): void {
    const sessionData: SessionData = {
      ...data,
      expiresAt: Date.now() + this.SESSION_DURATION,
      isLoggedIn: true
    } as SessionData;

    const encryptedData = this.encrypt(JSON.stringify(sessionData));
    sessionStorage.setItem(this.SESSION_KEY, encryptedData);
  }

  getSession(): SessionData | null {
    const encryptedData = sessionStorage.getItem(this.SESSION_KEY);

    if (!encryptedData) {
      return null;
    }

    try {
      const decryptedData = this.decrypt(encryptedData);
      const session: SessionData = JSON.parse(decryptedData);

      // Check if session expired
      if (session.expiresAt && Date.now() > session.expiresAt) {
        this.clearSession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('Session decryption failed:', error);
      this.clearSession();
      return null;
    }
  }

  updateSession(updates: Partial<SessionData>): void {
    const currentSession = this.getSession();
    if (currentSession) {
      this.setSession({ ...currentSession, ...updates });
    }
  }

  clearSession(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  isSessionValid(): boolean {
    const session = this.getSession();
    return session !== null && session.isLoggedIn === true;
  }

  getSessionProperty<K extends keyof SessionData>(key: K): SessionData[K] | null {
    const session = this.getSession();
    return session ? session[key] : null;
  }

  getOrganizationId(): any {
    return this.getSessionProperty('organizationId');
  }

  isSuperAdmin(): boolean {
    return this.getSessionProperty('isSuperAdmin') === true;
  }
}
